import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FadeUp, SlideInLeft, SlideInRight, ScaleIn, StaggerContainer, AnimatedCounter } from "@/components/base/ScrollAnimations";
import { companyInfo, services, stats, testimonials, whyChooseUs, productRange, products } from "@/mocks/websiteData";

export default function Home() {
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[540px] md:h-[720px] flex items-center justify-center overflow-hidden pt-20 md:pt-24">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover"
        >
          <source
            src="https://storage.readdy-site.link/project_files/c62b2dbe-6afd-4e68-a9dd-19baefdfb93f/2b87fac9-2bbe-4826-80bc-258cdb066b7e_Agricultural-field-with-drone-flying-over-crops-solar-panels-prominently-featured-and-highlighted-in.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/35"></div>

        <div className="relative z-10 section-padding w-full text-center">
          <div className={`transition-all duration-1000 ease-out ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/25 backdrop-blur-sm border border-accent-400/30 mb-6 drop-shadow-md">
              <i className="ri-award-line w-4 h-4 flex items-center justify-center text-accent-400"></i>
              <span className="text-sm font-medium text-accent-300">Govt. Registered | GST: {companyInfo.gstNo}</span>
            </div>
          </div>

          <div className={`transition-all duration-1000 ease-out delay-200 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <h1 className="font-heading font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-background-50 leading-tight mx-auto mb-2 drop-shadow-lg whitespace-nowrap">
              NexSunNexova Solar Energy Pvt. Ltd.
            </h1>
            <p className="font-heading font-semibold text-lg md:text-2xl lg:text-3xl text-accent-300 max-w-3xl mx-auto mb-4 drop-shadow-md tracking-wide">
              Solar Energy · Drone Spray · EV Charging
            </p>
          </div>

          <div className={`transition-all duration-1000 ease-out delay-400 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <p className="text-base md:text-lg text-background-100 max-w-2xl mx-auto mb-8 leading-relaxed drop-shadow-md">
              {companyInfo.shortName} delivers end-to-end solar installations, precision agricultural drone spraying, and EV charging infrastructure across Telangana. One partner for all your clean energy and smart farming needs.
            </p>
          </div>

          <div className={`transition-all duration-1000 ease-out delay-600 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-all whitespace-nowrap shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5"
              >
                <i className="ri-calculator-line w-4 h-4 flex items-center justify-center"></i>
                <span>Get Free Quote</span>
              </Link>
              <Link
                to="/products"
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/15 text-white font-medium hover:bg-white/25 backdrop-blur-sm transition-all border border-white/25 whitespace-nowrap hover:-translate-y-0.5"
              >
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
                <span>View Pricing</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-background-50 py-10 md:py-14">
        <div className="section-padding">
          <StaggerContainer staggerDelay={120} className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-4 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/40 transition-all hover:-translate-y-1">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                <p className="text-sm text-foreground-600 mt-2 font-medium">{stat.label}</p>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Founder / CEO Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-12 md:mb-16">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Leadership</span>
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 mt-3">Meet Our Founder</h2>
            </div>
          </FadeUp>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <SlideInLeft>
                <div className="relative">
                  {/* Background accent shape */}
                  <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl bg-primary-100/50"></div>
                  <div className="relative rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 aspect-[4/5] max-w-md mx-auto">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/1f8780b95eea472df48319064d2eefe4.png"
                      alt="Shaik Hyder Pasha - Founder &amp; Managing Director of NexSunNexova"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </SlideInLeft>

              <SlideInRight delay={150}>
                <div className="max-w-xl">
                  <h3 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mb-1">Shaik Hyder Pasha</h3>
                  <p className="text-base text-primary-600 font-semibold mb-6">Founder &amp; Managing Director</p>

                  <blockquote className="relative pl-5 border-l-4 border-accent-400 mb-6">
                    <p className="text-base md:text-lg text-foreground-600 leading-relaxed italic">
                      "Our mission is simple — make clean solar energy affordable and accessible to every home, farm, and business in Telangana."
                    </p>
                  </blockquote>

                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    With a vision to democratize solar energy access in Telangana, Shaik Hyder Pasha established NexSunNexova to bridge the gap between advanced solar technology and everyday consumers. His hands-on approach ensures every project meets the highest standards of quality and customer satisfaction.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                    Under his leadership, the company has expanded beyond solar installations into precision agricultural drone spraying and EV charging infrastructure — creating a comprehensive clean energy and smart farming ecosystem for homes, farms, and businesses across the state.
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <a href="tel:9603919373" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all shadow-md shadow-primary-500/20 whitespace-nowrap">
                      <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                      9603919373
                    </a>
                    <a href="tel:7893549373" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-all whitespace-nowrap">
                      <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                      7893549373
                    </a>
                  </div>
                </div>
              </SlideInRight>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Board of Directors</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mt-3">Our Leadership Team</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">The dedicated professionals driving NexSunNexova's growth across solar, drone, and EV verticals.</p>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={120} className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* Syed Salman */}
            <FadeUp delay={0}>
              <div className="group text-center p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/70 hover:border-accent-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="relative mx-auto mb-5 w-36 h-36 md:w-40 md:h-40">
                  <div className="absolute inset-0 rounded-full bg-accent-200/40 group-hover:bg-accent-300/40 transition-all"></div>
                  <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-background-50 shadow-md">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/0a125b9546dcdbd30d9b312373fcb47a.jpeg"
                      alt="Syed Salman - Director, Solar &amp; Drone Operations"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">Syed Salman</h3>
                <p className="text-sm text-accent-600 font-medium mb-3">Director, Solar &amp; Drone Operations</p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Leading NexSunNexova's solar EPC and agricultural drone spraying divisions with hands-on expertise in clean energy and precision agriculture.
                </p>
                <a href="tel:9542760314" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-50 text-accent-600 text-sm font-medium hover:bg-accent-100 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  9542760314
                </a>
              </div>
            </FadeUp>

            {/* MD. Sharfoddin */}
            <FadeUp delay={100}>
              <div className="group text-center p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="relative mx-auto mb-5 w-36 h-36 md:w-40 md:h-40">
                  <div className="absolute inset-0 rounded-full bg-primary-200/40 group-hover:bg-primary-300/40 transition-all"></div>
                  <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-background-50 shadow-md">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/7052c0aaaefcd4d63234395b6dcb00e3.jpeg"
                      alt="MD. Sharfoddin - Telangana Marketing Head, E.V Charging Stations"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">MD. Sharfoddin</h3>
                <p className="text-sm text-primary-600 font-medium mb-3">Telangana Marketing Head, E.V Charging Stations</p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Key strategist behind NexSunNexova's operational excellence and business expansion across Telangana.
                </p>
                <a href="tel:6300774817" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium hover:bg-primary-100 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  6300774817
                </a>
              </div>
            </FadeUp>

            {/* Shaik Hafeez */}
            <FadeUp delay={200}>
              <div className="group text-center p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/70 hover:border-secondary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="relative mx-auto mb-5 w-36 h-36 md:w-40 md:h-40">
                  <div className="absolute inset-0 rounded-full bg-secondary-200/40 group-hover:bg-secondary-300/40 transition-all"></div>
                  <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-background-50 shadow-md">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/9dedf6844aa349e4498aa61984a00807.jpeg"
                      alt="Shaik Hafeez - Drone Operations, TS &amp; AP Marketing Head"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">Shaik Hafeez</h3>
                <p className="text-sm text-secondary-600 font-medium mb-3">Drone Operations, TS &amp; AP Marketing Head</p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Driving brand presence and customer outreach across Telangana and Andhra Pradesh markets.
                </p>
                <a href="tel:9581383871" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-50 text-secondary-600 text-sm font-medium hover:bg-secondary-100 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  9581383871
                </a>
              </div>
            </FadeUp>
          </StaggerContainer>
        </div>
      </section>

      {/* PM KUSUM Yojana Highlight */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="max-w-5xl mx-auto relative rounded-2xl overflow-hidden bg-primary-500 shadow-lg shadow-primary-500/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="p-6 md:p-8 lg:p-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-4">
                    <i className="ri-government-line w-4 h-4 flex items-center justify-center text-accent-300"></i>
                    <span className="text-xs font-semibold text-background-100">Government Scheme</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-background-50 mb-2">PM KUSUM Yojana Megha Project</h3>
                  <p className="text-sm text-primary-100 leading-relaxed mb-5">
                    Telangana's agricultural solar initiative by TGREDCO. Solar plants, solar pumps, and pump solarization for farmers — with subsidy support and guaranteed power buyback.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-sm text-primary-100">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-300 shrink-0"></i>
                      <span>500 kW to 2 MW solar plants @ ₹3.13/kWh</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-primary-100">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-300 shrink-0"></i>
                      <span>Off-grid solar pumps with subsidy</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-primary-100">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-300 shrink-0"></i>
                      <span>Existing pump solarization available</span>
                    </div>
                  </div>
                  <Link to="/services" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-all whitespace-nowrap">
                    Learn More About PM KUSUM
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
                <div className="hidden md:block relative h-64 md:h-auto overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Solar%20power%20plant%20on%20dry%20agricultural%20land%20in%20India%20with%20farmer%20inspecting%20solar%20panels%2C%20golden%20hour%20warm%20lighting%2C%20rows%20of%20blue%20panels%20on%20brown%20barren%20land%2C%20rural%20setting%2C%20professional%20photography%2C%20sustainable%20agriculture&width=600&height=450&seq=pmkusum-home-banner-01&orientation=landscape"
                    alt="PM KUSUM solar plant on farmland"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/60 to-transparent"></div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Our Three Core Services */}
      <section className="py-16 md:py-24 bg-background-50 overflow-hidden">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-12 md:mb-16">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">What We Sell</span>
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 mt-3">Three Solutions, One Partner</h2>
              <p className="text-foreground-600 mt-4 max-w-2xl mx-auto text-base md:text-lg">
                We design, supply, install, and maintain solar systems, agricultural drones, and EV chargers across Telangana. Everything under one roof.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto items-stretch">
            {/* Solar Energy Card */}
            <FadeUp delay={0}>
              <div className="h-full flex flex-col group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all hover:-translate-y-2 shadow-sm hover:shadow-lg">
                <div className="h-52 md:h-60 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Large%20modern%20solar%20power%20plant%20with%20rows%20of%20blue%20solar%20panels%20stretching%20across%20green%20agricultural%20landscape%20in%20India%2C%20dramatic%20golden%20sunset%20sky%20with%20warm%20orange%20and%20pink%20clouds%2C%20long%20shadows%20on%20the%20ground%2C%20professional%20aerial%20landscape%20photography%2C%20cinematic%20composition%2C%20vibrant%20colors%2C%20clean%20energy%20future&width=600&height=400&seq=solar-core-01&orientation=landscape&nocache=true"
                    alt="Solar Energy Solutions"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="flex flex-col flex-1 p-6 md:p-7">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-sun-line w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-2">Solar Energy</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    On-grid, off-grid, and commercial solar installations. Residential rooftops to large-scale EPC projects up to 150 kW. Subsidy assistance included.
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>EPC from ₹66,000/kW with installation</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Govt. subsidy up to ₹78,000</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>25-year panel warranty + AMC</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Link
                      to="/products"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all shadow-md shadow-primary-500/20 whitespace-nowrap"
                    >
                      View Solar Pricing
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Drone Spray Card */}
            <FadeUp delay={100}>
              <div className="h-full flex flex-col group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-300/50 transition-all hover:-translate-y-2 shadow-sm hover:shadow-lg">
                <div className="h-52 md:h-60 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Close-up%20shot%20of%20agricultural%20drone%20flying%20low%20over%20lush%20green%20crop%20field%20spraying%20fine%20liquid%20mist%20and%20droplets%20clearly%20visible%20in%20golden%20sunlight%2C%20precision%20agriculture%20technology%2C%20professional%20photography%2C%20high%20detail%2C%20vibrant%20green%20crops%20with%20visible%20water%20droplets%20on%20leaves&width=600&height=400&seq=drone-core-01&orientation=landscape&nocache=true"
                    alt="Agricultural Spray Drones"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="flex flex-col flex-1 p-6 md:p-7">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-flight-takeoff-line w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-2">Drone Spray</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    Precision agricultural drones for liquid fertilizer and pesticide spraying. Cover 5 acres in 30 minutes with 30% less chemical use.
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>10L to 20L capacity drones from ₹1.85L</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Training + AMC + spare parts support</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Service available across Telangana</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Link
                      to="/products"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-all shadow-md shadow-accent-500/20 whitespace-nowrap"
                    >
                      View Drone Pricing
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* EV Charging Card */}
            <FadeUp delay={200}>
              <div className="h-full flex flex-col group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-secondary-300/50 transition-all hover:-translate-y-2 shadow-sm hover:shadow-lg">
                <div className="h-52 md:h-60 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20EV%20electric%20vehicle%20charging%20station%20in%20clean%20residential%20driveway%2C%20sleek%20white%20charger%20unit%20with%20glowing%20blue%20LED%20ring%2C%20electric%20car%20plugged%20in%20charging%2C%20warm%20sunset%20light%2C%20professional%20product%20photography%2C%20clean%20modern%20aesthetic&width=600&height=400&seq=ev-core-01&orientation=landscape&nocache=true"
                    alt="EV Charging Stations"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="flex flex-col flex-1 p-6 md:p-7">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-charging-pile-line w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-2">EV Charging</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    AC home chargers to DC fast charging stations. 3.3 kW home units to 120 kW commercial DC fast chargers with OCPP smart management.
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>AC home from ₹19,150 | DC fast up to 480 kW (GST incl.)</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>9 models: 3.3 kW to 480 kW</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Installation + OCPP + subsidy support</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Link
                      to="/products"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-all shadow-md shadow-secondary-500/20 whitespace-nowrap"
                    >
                      View EV Pricing
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* All Services Overview */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Complete Portfolio</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">All Services &amp; Solutions</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">Beyond our three core offerings, we also provide additional solar solutions to meet every need.</p>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={100} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {services.slice(0, 6).map((service) => (
              <div key={service.id} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-44 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className={`${service.icon} w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{service.description}</p>
                  <Link to="/services" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                    Learn more <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            ))}
          </StaggerContainer>

          <FadeUp delay={200}>
            <div className="text-center mt-10">
              <Link to="/services" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-md shadow-primary-500/20">
                View All Services <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Core Products Quick View */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Our Products</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Featured Product Lines</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">From solar EPC kits and drone systems to EV chargers — transparent pricing on everything.</p>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={80} className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
            {[
              { name: "Solar EPC Systems", icon: "ri-sun-line", desc: "3 kW to 150 kW turnkey installations", price: "From ₹66,000/kW", color: "primary" },
              { name: "Spray Drones", icon: "ri-flight-takeoff-line", desc: "10L to 20L agricultural drones", price: "From ₹1,85,000", color: "accent" },
              { name: "EV Chargers", icon: "ri-charging-pile-line", desc: "3.3 kW AC home to 480 kW DC fast", price: "From ₹19,150 (GST incl.)", color: "secondary" },
            ].map((product) => (
              <div key={product.name} className="flex flex-col items-center text-center p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 hover:bg-background-50 transition-all hover:-translate-y-1 shadow-sm">
                <div className={`w-14 h-14 flex items-center justify-center rounded-full bg-${product.color}-100 text-${product.color}-600 mb-3`}>
                  <i className={`${product.icon} w-7 h-7 flex items-center justify-center text-xl`}></i>
                </div>
                <h4 className="font-heading font-semibold text-base text-foreground-900 mb-1">{product.name}</h4>
                <p className="text-xs text-foreground-500 mb-2">{product.desc}</p>
                <span className="text-sm font-semibold text-primary-600">{product.price}</span>
              </div>
            ))}
          </StaggerContainer>

          <FadeUp delay={200}>
            <div className="text-center mt-10">
              <Link to="/products" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-medium hover:bg-primary-500 hover:text-background-50 transition-all hover:-translate-y-0.5">
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
                <span>View Full Price List</span>
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Top 3 Product Lines - Featured Cards with Tables */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Featured Products</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Our Top 3 Product Lines</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">Transparent pricing on our primary sales offerings. All prices are exclusive of GST.</p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto items-stretch">
            {/* EV Chargers Featured */}
            <FadeUp delay={0}>
              <div className="h-full flex flex-col bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-secondary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-48 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20EV%20charging%20station%20unit%20in%20sleek%20parking%20lot%2C%20multiple%20charger%20posts%20with%20LED%20status%20indicators%2C%20electric%20car%20nearby%2C%20clean%20professional%20product%20photography%2C%20bright%20daylight%2C%20high%20tech%20futuristic%20design&width=600&height=400&seq=product-ev-featured-home-01&orientation=landscape"
                    alt="EV Charging Stations"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col flex-1 p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-foreground-900">EV Charging Stations</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">New Arrival</span>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4">AC and DC fast chargers from 3.3 kW home units to 480 kW commercial stations. Prices include GST.</p>
                  <div className="flex-1 overflow-hidden rounded-lg border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-secondary-100">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-700">Model</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price (incl. GST)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">3.3 kW AC Home</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹19,150</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">7.4 kW AC Wallbox</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹40,505</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">12 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹3,30,500</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">60 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹11,31,140</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">120 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹16,48,440</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">180 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹19,46,640</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">240 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹22,56,090</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">360 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹35,42,340</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">480 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹39,91,740</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Drone Spray Featured */}
            <FadeUp delay={100}>
              <div className="h-full flex flex-col bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-48 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20agricultural%20spray%20drone%20on%20ground%20in%20green%20paddy%20field%2C%20folded%20arms%2C%20orange%20and%20black%20design%2C%20spraying%20tank%20visible%2C%20morning%20golden%20light%2C%20professional%20product%20photography%2C%20high%20detail&width=600&height=400&seq=product-drone-featured-home-01&orientation=landscape"
                    alt="Agricultural Spray Drones"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col flex-1 p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-foreground-900">Agricultural Spray Drones</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">Farmer's Choice</span>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4">Precision spraying drones with training, spare parts, and AMC packages included.</p>
                  <div className="flex-1 overflow-hidden rounded-lg border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-accent-100">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-700">Model</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">10L Spray Drone</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹1,85,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">16L Spray Drone</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹2,45,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">20L Spray Drone</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹3,15,000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Solar EPC Featured */}
            <FadeUp delay={200}>
              <div className="h-full flex flex-col bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-48 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20solar%20panel%20installation%20on%20Indian%20residential%20rooftop%2C%20worker%20installing%20panels%2C%20safety%20gear%2C%20clean%20bright%20daylight%2C%20professional%20photography&width=600&height=400&seq=product-epc-featured-home-01&orientation=landscape"
                    alt="Solar EPC Systems"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col flex-1 p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-foreground-900">Solar EPC Systems</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Most Popular</span>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4">Turnkey solar installations from 3 kW to 150 kW. Panels, inverters, mounting, and commissioning included.</p>
                  <div className="flex-1 overflow-hidden rounded-lg border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-primary-100">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-700">Capacity</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price/kW</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">3-6 kW (1P)</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹66,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">7-9 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹61,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">10-20 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹56,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">21-50 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹54,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">51-100 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹52,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">101-150 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹50,000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Solar Product Price List */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Pricing</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Solar Product Price List</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">Transparent, up-to-date pricing across all solar product categories. GST extra where applicable.</p>
            </div>
          </FadeUp>

          <div className="max-w-6xl mx-auto space-y-8 md:space-y-10">
            {/* EPC On-Grid */}
            {(() => {
              const epc = products.find(p => p.id === "epc-on-grid");
              if (!epc) return null;
              return (
                <FadeUp>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-sun-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{epc.category}</h3>
                          {epc.badge && <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">{epc.badge}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{epc.description}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-primary-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">Capacity</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {epc.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === epc.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* DIY On-Grid */}
            {(() => {
              const diy = products.find(p => p.id === "diy-on-grid");
              if (!diy) return null;
              return (
                <FadeUp delay={100}>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className="ri-tools-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{diy.category}</h3>
                          {diy.badge && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{diy.badge}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{diy.description}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-secondary-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">Capacity</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diy.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === diy.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* Solar Water Pumps */}
            {(() => {
              const pumps = products.find(p => p.id === "solar-pumps");
              if (!pumps) return null;
              return (
                <FadeUp delay={150}>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className="ri-water-flash-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{pumps.category}</h3>
                          {pumps.badge && <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">{pumps.badge}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{pumps.description}</p>
                    </div>
                    <div className="p-5 md:px-7 md:pb-3 bg-accent-50 border-b border-accent-200/50">
                      <div className="flex items-center gap-2 text-sm">
                        <i className="ri-gift-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                        <span className="font-semibold text-accent-700">Government Subsidy: {companyInfo.subsidyAmount}</span>
                        <span className="text-accent-600">available under PM Surya Ghar &amp; state schemes</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-accent-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">HP</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pumps.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === pumps.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* Solar Batteries */}
            {(() => {
              const batteries = products.find(p => p.id === "solar-batteries");
              if (!batteries) return null;
              return (
                <FadeUp delay={200}>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className="ri-battery-2-charge-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{batteries.category}</h3>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{batteries.description}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-secondary-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">Battery</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batteries.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === batteries.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* Terms */}
            <FadeUp delay={250}>
              <div className="max-w-4xl mx-auto p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0"></i>
                  <div>
                    <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">Terms &amp; Conditions</h3>
                    <ul className="space-y-1.5 text-sm text-foreground-600">
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>GST extra where applicable.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>Prices subject to change based on market rates and availability.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>MOQ (Minimum Order Quantity) as per availability.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>EPC pricing includes installation unless stated otherwise.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>Subsidy amount may vary based on government scheme eligibility and capacity.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={300}>
            <div className="text-center mt-10">
              <Link to="/products" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-medium hover:bg-primary-500 hover:text-background-50 transition-all hover:-translate-y-0.5">
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
                <span>View Full Price List</span>
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Why Us</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Why Choose NexSunNexova?</h2>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={100} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {whyChooseUs.map((item) => (
              <div key={item.title} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                  <i className={`${item.icon} w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Testimonials</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">What Our Customers Say</h2>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={150} className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
            {testimonials.map((t) => (
              <div key={t.name} className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/30 transition-all">
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <span key={j} className="w-4 h-4 flex items-center justify-center text-sm">
                      {j < t.rating ? (
                        <i className="ri-star-fill text-primary-400"></i>
                      ) : (
                        <i className="ri-star-line text-background-300"></i>
                      )}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 font-semibold text-sm">
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20vibrant%20Indian%20city%20skyline%20with%20solar%20panels%20on%20residential%20rooftops%2C%20commercial%20buildings%2C%20and%20industrial%20warehouses%2C%20all%20under%20bright%20golden%20sunset%2C%20dramatic%20colorful%20sky%2C%20clean%20energy%20future%20vision%2C%20professional%20drone%20photography%2C%20wide%20sweeping%20landscape&width=1600&height=700&seq=cta-all-areas-01&orientation=landscape"
          alt="Solar panels across city — homes, commercial buildings, and industrial rooftops"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-foreground-900/75"></div>
        <div className="relative z-10 section-padding text-center">
          <FadeUp>
            <h2 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-background-50 mb-4">
              Ready to Go Clean &amp; Smart?
            </h2>
          </FadeUp>
          <FadeUp delay={100}>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8">
              Get a free consultation for solar, drone spraying, or EV charging. Our experts will guide you through products, pricing, subsidies, and installation — all at zero cost.
            </p>
          </FadeUp>
          <FadeUp delay={200}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-all whitespace-nowrap shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5"
              >
                <i className="ri-calendar-check-line w-5 h-5 flex items-center justify-center"></i>
                <span>Book Free Site Survey</span>
              </Link>
              <a
                href="tel:8019051311"
                className="flex items-center gap-2 px-8 py-4 rounded-full bg-white/15 text-white font-medium hover:bg-white/25 backdrop-blur-sm transition-all border border-white/25 whitespace-nowrap hover:-translate-y-0.5"
              >
                <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                <span>Call 8019051311</span>
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}